import { useCallback, useState } from 'react';
import { Alert } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { ContentTemplateIdType } from '@tiendanube/common';
import HelpLink from 'App/HelpLink';
import { useNavegate } from 'App/hooks';
import { LanguageNameValueInterface } from 'App/i18n';
import { HeaderContent, HeaderTop, IonPageStratus } from 'commons/components';
import { useForm, useModal, useToastStatus } from 'commons/hooks';
import { useGetAllLanguages } from 'domains/Auth/hooks';
import useTranslationOnline from 'domains/Online/useTranslationOnline';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { CONTENT_PAGES_ROUTES } from '../../contentPageRoutes';
import { useCreateContentPages } from '../../hooks';
import { validationSchema } from '../../validationSchema';
import {
  DesignEditorLaunchModal,
  FormPage,
  FormPageSkeleton,
} from '../components';
import FormFooter from '../components/FormPage/components/FormFooter';
import { useDesignEditorLaunchModal } from '../hooks';

const PAGES_WITH_EMPTY_SUGGESTED_CONTENT = ['blank'];

function PageCreation() {
  const [accepted, setAccepted] = useState(false);
  const [language] = useGetAllLanguages();
  const { goBack, getQueryParam, replace, buildPath } = useNavegate();
  const templateId =
    (getQueryParam('templateId') as ContentTemplateIdType) || 'blank';
  const [showPrivacyPolicy, , closePrivacyPolicy] = useModal(
    templateId === 'privacyPolicy',
  );
  const t = useTranslationOnline('contentPages');
  const { t: translate } = useTranslation();
  const { initialValues, status, statusTemplate, createPage } =
    useCreateContentPages(templateId);

  const {
    showDesignModal,
    themesConfig,
    handleOpenEditDesignModal,
    handleCloseEditDesignModal,
    handleDesignEditModalSubmit,
  } = useDesignEditorLaunchModal({
    submitFn: createPage,
    onSubmitSuccess: (pageId) => {
      replace(buildPath(CONTENT_PAGES_ROUTES.pageEditing, { id: pageId }));
    },
  });

  const form = useForm({
    initialValues,
    validationSchema: validationSchema(language),
    onSubmit: async (data) => {
      if (data) {
        const id = await createPage(data);
        replace(
          buildPath(CONTENT_PAGES_ROUTES.pageEditing, { id: String(id) }),
        );
      }
    },
  });

  useToastStatus({
    error: t('formPage.toast.edit.error'),
    success: t('formPage.toast.edit.success'),
    status,
  });

  useToastStatus({
    error: t('formPage.toast.template.error'),
    onError: goBack,
    status: statusTemplate,
  });

  const handleChange = useCallback(
    ({ name, value }: LanguageNameValueInterface) => {
      form.setFieldValue(name, value);
    },
    [form],
  );

  const handleClose = () => {
    closePrivacyPolicy();
    goBack();
  };

  const handleShowInStoreChange = useCallback(
    (value: boolean) => {
      form.setFieldValue('showInStore', value);
    },
    [form],
  );

  return (
    <IonPageStratus
      width="small"
      headerContent={<HeaderContent title={t('formPage.createPage')} />}
      headerTop={<HeaderTop navigation={{ onClick: goBack }} />}
    >
      <Alert show={accepted && !showPrivacyPolicy} appearance="warning">
        {t('privacyPolicy.alert')}
      </Alert>
      <Prompt
        when={form.isDirty && !showDesignModal}
        message={translate('common:exitEdit.info')}
      />
      {statusTemplate === 'loading' && <FormPageSkeleton />}
      {statusTemplate !== 'loading' && statusTemplate !== 'error' && (
        <>
          <FormPage
            values={form.values}
            errors={form.errors}
            themesConfig={themesConfig}
            onChange={handleChange}
            onDesignEditClick={handleOpenEditDesignModal}
          />

          <FormFooter
            values={form.values}
            onShowInStoreChange={handleShowInStoreChange}
            onCancel={goBack}
            onSave={form.handleOnSubmit}
            saveText={t('formPage.createButton')}
            isLoading={status === 'loading'}
            isDisabled={
              PAGES_WITH_EMPTY_SUGGESTED_CONTENT.includes(templateId) &&
              !form.isDirty
            }
          />
          <HelpLink
            title={t('helpLink.title')}
            linkURL={t('helpLink.linkURL')}
            previousValue=""
            currentViewTracking={t('helpLink.currentViewTracking')}
            icon="both"
          />
        </>
      )}
      <PrivacyPolicyModal
        isShow={showPrivacyPolicy}
        onClose={handleClose}
        onChange={setAccepted}
        checked={accepted}
        onAccept={closePrivacyPolicy}
      />

      {showDesignModal && !!themesConfig && (
        <DesignEditorLaunchModal
          language={language}
          initialValues={form.values}
          themesConfig={themesConfig}
          isSubmitting={status === 'loading'}
          onClose={handleCloseEditDesignModal}
          onSubmit={handleDesignEditModalSubmit}
        />
      )}
    </IonPageStratus>
  );
}

export default PageCreation;
